import type RAPIER_NS from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { bucketTriangles, cellKey, cellsAroundDist, compactTriangles } from './cells';

export type Rapier = typeof RAPIER_NS;
type Collider = RAPIER_NS.Collider;

/** What a collider stands for: ray hits report it (e.g. the car spawns on 'roads'). */
export type ColliderTag = 'terrain' | 'roads' | 'solid' | 'ground' | 'facade' | 'interior' | 'furniture' | 'window' | 'tree' | 'lamp' | 'dynamic';

/**
 * A scene mesh that collides. 'tri': its triangles (a static trimesh per column); 'box': each instance's
 * bounding box (windows, furniture); 'trunk': each instance as a vertical cylinder of `radius` from its
 * base up to `height` (trees, lamp posts).
 */
export interface CollisionSource {
  mesh: THREE.Mesh;
  tag: ColliderTag;
  shape: 'tri' | 'box' | 'trunk' | 'batch';
  radius?: number;
  height?: number;
}

interface InstShape {
  box?: { hx: number; hy: number; hz: number };
  cyl?: { hh: number; r: number };
  p: THREE.Vector3;
  q: THREE.Quaternion;
}

interface Bucketed {
  /** World-space XZ bounds of the whole mesh. */
  box: THREE.Box3;
  tris?: { positions: Float32Array; indices: ArrayLike<number>; cells: Map<string, Uint32Array> };
  inst?: Map<string, InstShape[]>;
}

/** Column size (m) of the collision stream. */
export const CELL = 24;

/**
 * Rapier world with static colliders streamed from the meshes already in the scene near a focus
 * point (the walker or the car): world-space triangles bucketed per 24 m column and one trimesh per
 * (mesh, column); instances as boxes or cylinders. Columns are built under a time budget per frame
 * and dropped when their mesh leaves the scene or the focus moves away (DECISIONS "Navigation").
 */
export class Physics {
  readonly world: RAPIER_NS.World;
  /** Collider handle → what it is. */
  readonly tags = new Map<number, ColliderTag>();
  /** (mesh uuid | column) → its colliders and when it was last wanted. */
  private readonly built = new Map<string, { colliders: Collider[]; wanted: number }>();
  private readonly buckets = new WeakMap<THREE.Mesh, Bucketed>();
  /** Columns wanted near the focus that are not built yet (0 = the area is ready). */
  pending = 0;
  /** Of those, the ones within the `near` distance given to stream() (0 = the middle is ready). */
  pendingNear = 0;
  private acc = 0;
  readonly step = 1 / 60;

  constructor(readonly R: Rapier) {
    this.world = new R.World({ x: 0, y: -9.81, z: 0 });
    this.world.timestep = this.step;
  }

  static async load(): Promise<Physics> {
    const R = (await import('@dimforge/rapier3d-compat')).default;
    await R.init();
    return new Physics(R);
  }

  /**
   * Stream colliders for `sources` around (x, z) within `radius` m, spending at most `budgetMs`.
   * Columns are built nearest first; `near` (m) sets which of the missing ones count as pendingNear.
   */
  stream(sources: CollisionSource[], x: number, z: number, radius: number, budgetMs: number, near = 0): void {
    const t0 = performance.now();
    const now = t0;
    const cells = cellsAroundDist(x, z, radius, CELL);
    const wanted = new Set<string>();
    let pending = 0;
    let pendingNear = 0;
    // Each mesh's triangles or instances bucketed by column (once per mesh).
    const ready: { s: CollisionSource; b: Bucketed }[] = [];
    for (const s of sources) {
      let b = this.buckets.get(s.mesh);
      if (!b) {
        const box = worldBox(s.mesh);
        if (!box || !nearBox(box, x, z, radius)) continue;
        if (performance.now() - t0 > budgetMs) {
          pending++;
          if (nearBox(box, x, z, near)) pendingNear++;
          continue;
        }
        b = bucket(s, box);
        this.buckets.set(s.mesh, b);
      }
      if (nearBox(b.box, x, z, radius)) ready.push({ s, b });
    }
    for (const { key: c, d } of cells) {
      for (const { s, b } of ready) {
        const has = b.tris ? b.tris.cells.has(c) : b.inst!.has(c);
        if (!has) continue;
        const key = `${s.mesh.uuid}|${c}`;
        wanted.add(key);
        const had = this.built.get(key);
        if (had) {
          had.wanted = now;
          continue;
        }
        if (performance.now() - t0 > budgetMs) {
          pending++;
          if (d <= near) pendingNear++;
          continue;
        }
        this.built.set(key, { colliders: this.build(s, b, c), wanted: now });
      }
    }
    // Kept a second after they're last wanted: when a tile swaps its city mesh (detail level), the old
    // colliders hold the player up until the new ones are built.
    for (const [key, e] of this.built) {
      if (wanted.has(key) || now - e.wanted < 1000) continue;
      for (const c of e.colliders) {
        this.tags.delete(c.handle);
        this.world.removeCollider(c, false);
      }
      this.built.delete(key);
    }
    this.pending = pending;
    this.pendingNear = pendingNear;
  }

  private build(s: CollisionSource, b: Bucketed, cell: string): Collider[] {
    const R = this.R;
    const out: Collider[] = [];
    const add = (d: RAPIER_NS.ColliderDesc) => {
      const c = this.world.createCollider(d);
      this.tags.set(c.handle, s.tag);
      out.push(c);
    };
    if (b.tris) {
      const { vertices, indices } = compactTriangles(b.tris.positions, b.tris.indices, b.tris.cells.get(cell)!);
      if (indices.length >= 3) add(R.ColliderDesc.trimesh(vertices, indices));
    } else {
      for (const sh of b.inst!.get(cell)!) {
        const d = sh.box ? R.ColliderDesc.cuboid(sh.box.hx, sh.box.hy, sh.box.hz) : R.ColliderDesc.cylinder(sh.cyl!.hh, sh.cyl!.r);
        add(d.setTranslation(sh.p.x, sh.p.y, sh.p.z).setRotation({ x: sh.q.x, y: sh.q.y, z: sh.q.z, w: sh.q.w }));
      }
    }
    return out;
  }

  /**
   * Advance the simulation by `dt` (at most 6 steps), calling `tick` before and `after` after each
   * fixed step. Returns the fraction of a step left over, for interpolating the last two poses.
   */
  advance(dt: number, tick: (h: number) => void, after?: () => void): number {
    this.acc = Math.min(this.acc + dt, this.step * 6);
    while (this.acc >= this.step) {
      tick(this.step);
      this.world.step();
      after?.();
      this.acc -= this.step;
    }
    return this.acc / this.step;
  }

  /** First hit of a ray (world space) within `max` m, excluding `exclude`'s colliders. */
  cast(from: THREE.Vector3, dir: THREE.Vector3, max: number, exclude?: RAPIER_NS.RigidBody): { toi: number; tag: ColliderTag | undefined; normal: THREE.Vector3 } | null {
    const ray = new this.R.Ray({ x: from.x, y: from.y, z: from.z }, { x: dir.x, y: dir.y, z: dir.z });
    const hit = this.world.castRayAndGetNormal(ray, max, true, undefined, undefined, undefined, exclude);
    if (!hit) return null;
    return { toi: hit.timeOfImpact, tag: this.tags.get(hit.collider.handle), normal: new THREE.Vector3(hit.normal.x, hit.normal.y, hit.normal.z) };
  }

  get colliderCount(): number {
    return this.tags.size;
  }

  dispose(): void {
    this.world.free();
  }
}

function visible(o: THREE.Object3D): boolean {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return false;
  return true;
}

/**
 * Collision sources are only meshes in the scene and shown, or hidden on purpose as collision proxies
 * (`userData.collider`: instances drawn through LOD copies, TileManager.applyLod).
 */
export function usable(m: THREE.Object3D): boolean {
  let root: THREE.Object3D = m;
  while (root.parent) root = root.parent;
  return root instanceof THREE.Scene && (visible(m) || (m.userData.collider === true && visible(m.parent ?? m)));
}

function worldBox(m: THREE.Mesh): THREE.Box3 | null {
  if (m instanceof THREE.BatchedMesh || m instanceof THREE.InstancedMesh) {
    if (!m.boundingBox) m.computeBoundingBox();
    return m.boundingBox ? m.boundingBox.clone().applyMatrix4(m.matrixWorld) : null;
  }
  const g = m.geometry;
  if (!g.boundingBox) g.computeBoundingBox();
  return g.boundingBox ? g.boundingBox.clone().applyMatrix4(m.matrixWorld) : null;
}

function nearBox(b: THREE.Box3, x: number, z: number, r: number): boolean {
  const dx = Math.max(b.min.x - x, 0, x - b.max.x);
  const dz = Math.max(b.min.z - z, 0, z - b.max.z);
  return dx * dx + dz * dz <= r * r;
}

function bucket(s: CollisionSource, box: THREE.Box3): Bucketed {
  const m = s.mesh;
  m.updateWorldMatrix(true, false);
  if (s.shape === 'tri') {
    const src = m.geometry.getAttribute('position') as THREE.BufferAttribute;
    const positions = new Float32Array(src.count * 3);
    const v = new THREE.Vector3();
    for (let i = 0; i < src.count; i++) {
      v.fromBufferAttribute(src, i).applyMatrix4(m.matrixWorld);
      positions[i * 3] = v.x;
      positions[i * 3 + 1] = v.y;
      positions[i * 3 + 2] = v.z;
    }
    const index = m.geometry.getIndex();
    const indices: ArrayLike<number> = index ? (index.array as ArrayLike<number>) : Uint32Array.from({ length: src.count }, (_, i) => i);
    return { box, tris: { positions, indices, cells: bucketTriangles(positions, indices, CELL) } };
  }
  const inst = new Map<string, InstShape[]>();
  const addShape = (shape: InstShape) => {
    const key = cellKey(Math.floor(shape.p.x / CELL), Math.floor(shape.p.z / CELL));
    let l = inst.get(key);
    if (!l) inst.set(key, (l = []));
    l.push(shape);
  };
  if (m instanceof THREE.BatchedMesh) {
    // One box per piece from its model's bounds; ceiling fixtures (userData.fixture) are skipped.
    const fixture = (m.userData.fixture ?? []) as boolean[];
    const mi = new THREE.Matrix4();
    const w = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const q = new THREE.Quaternion();
    const sc = new THREE.Vector3();
    const gb = new THREE.Box3();
    for (let i = 0; i < m.instanceCount; i++) {
      if (fixture[i]) continue;
      m.getMatrixAt(i, mi);
      m.getBoundingBoxAt(m.getGeometryIdAt(i), gb);
      w.multiplyMatrices(m.matrixWorld, mi);
      w.decompose(p, q, sc);
      const c = gb.getCenter(new THREE.Vector3()).applyMatrix4(w);
      const size = gb.getSize(new THREE.Vector3()).multiply(sc);
      addShape({ box: { hx: Math.max(Math.abs(size.x) / 2, 0.03), hy: Math.max(Math.abs(size.y) / 2, 0.03), hz: Math.max(Math.abs(size.z) / 2, 0.03) }, p: c, q: q.clone() });
    }
    return { box, inst };
  }
  const im = m as THREE.InstancedMesh;
  const g = im.geometry;
  if (!g.boundingBox) g.computeBoundingBox();
  const gb = g.boundingBox!;
  const mi = new THREE.Matrix4();
  const w = new THREE.Matrix4();
  const p = new THREE.Vector3();
  const q = new THREE.Quaternion();
  const sc = new THREE.Vector3();
  for (let i = 0; i < im.count; i++) {
    im.getMatrixAt(i, mi);
    w.multiplyMatrices(im.matrixWorld, mi);
    w.decompose(p, q, sc);
    let shape: InstShape;
    if (s.shape === 'box') {
      const c = gb.getCenter(new THREE.Vector3()).applyMatrix4(w);
      const size = gb.getSize(new THREE.Vector3()).multiply(sc);
      shape = { box: { hx: Math.max(Math.abs(size.x) / 2, 0.03), hy: Math.max(Math.abs(size.y) / 2, 0.03), hz: Math.max(Math.abs(size.z) / 2, 0.03) }, p: c, q: q.clone() };
    } else {
      const hh = ((s.height ?? 3) * sc.y) / 2;
      shape = { cyl: { hh, r: (s.radius ?? 0.2) * sc.x }, p: new THREE.Vector3(p.x, p.y + hh, p.z), q: new THREE.Quaternion() };
    }
    addShape(shape);
  }
  return { box, inst };
}
